import { createRouter, createWebHistory } from 'vue-router';
import { useUserStore } from '@/stores/user';

const router = createRouter({
    mode: 'history',
    history: createWebHistory(import.meta.env.BASE_URL),
    base: import.meta.env.BASE_URL,
    routes: [
        {
            path: '/',
            redirect: '/home',
            children: [
                {
                    name: 'login',
                    path: 'login',
                    component: () =>
                        import(/* webpackChunkName: "login" */ '../views/user/LoginPage.vue'),
                    meta: { requiresAuth: false, isInMenu: false, noAnimation: true },
                    alias: ['register', 'password_recovery', 'email_activation', 'password_reset'],
                },
                {
                    name: 'auto-login',
                    path: 'auto-login',
                    component: () =>
                        import(/* webpackChunkName: "login" */ '../views/user/AutoLoginPage.vue'),
                    meta: { requiresAuth: false, isInMenu: false, noAnimation: true },
                },
                {
                    name: 'widget-expert',
                    path: 'widget-expert',
                    component: () => import('../views/widget/WidgetExpertView.vue'),
                    meta: { requiresAuth: false, isInMenu: false, adminMode: false },
                },
                {
                    name: 'group-management',
                    path: 'groups',
                    component: () =>
                        import(/* webpackChunkName: "login" */ '../views/user/UserManagement.vue'),
                    meta: { requiresAuth: true, isInMenu: false },
                },
                {
                    name: 'expert-share',
                    path: 's/:sharing_id',
                    component: () =>
                        import(
                            /* webpackChunkName: "expert-sharing-view" */ '../views/expert/ExpertSharingView.vue'
                        ),
                    meta: { requiresAuth: false, isInMenu: false },
                },
                {
                    name: 'polit-chat',
                    path: 'polit-chat',
                    component: () => import('../views/politchat/PolitChatView.vue'),
                    meta: { requiresAuth: false, isInMenu: false },
                },
                {
                    name: 'home',
                    path: 'home/:expert_id?',
                    component: () => import('../views/HomeView.vue'),
                    meta: { requiresAuth: true, isInMenu: true, narrowPadding: true },
                },
                {
                    name: 'agent',
                    path: 'agent',
                    component: () => import('../views/AgentView.vue'),
                    meta: { requiresAuth: true, isInMenu: true, narrowPadding: true },
                },
                {
                    name: 'dashboard-view',
                    path: 'my-experts',
                    component: () => import('../views/DashboardView.vue'),
                    meta: { requiresAuth: true, isInMenu: false },
                },
                {
                    name: 'expert-configurator',
                    path: 'expert-configurator/:expert_id/:tab?/:integration_type?',
                    component: () => import('../views/expert/ExpertConfiguratorView.vue'),
                    meta: { requiresAuth: true, isInMenu: false },
                    beforeEnter: (to, from, next) => {
                        if (!to.params.tab) {
                            next({
                                name: 'expert-configurator',
                                params: {
                                    expert_id: to.params.expert_id,
                                    tab: 'setup',
                                },
                                replace: true, // This replaces the current history entry instead of adding a new one
                            });
                        } else {
                            next();
                        }
                    },
                },
                {
                    name: 'expert-analytics',
                    path: 'expert-analytics/:expert_id',
                    component: () => import('../views/expert/ExpertAnalyticsView.vue'),
                    meta: { requiresAuth: true, isInMenu: false },
                },
                {
                    path: 'expert-evaluation/:expert_id',
                    component: () => import('../views/expert/ExpertEvaluationView.vue'),
                    meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                },
                {
                    name: 'agb-view',
                    path: 'agb',
                    component: () => import('../views/misc/AGBView.vue'),
                    meta: { requiresAuth: false, isInMenu: false },
                },
                {
                    name: 'processes-view',
                    path: 'processes',
                    component: () => import('../views/ProcessesView.vue'),
                    meta: { requiresAuth: true, isInMenu: false },
                },

                {
                    path: 'workspace',
                    component: () => import('../views/workspace/WorkspaceBaseView.vue'),
                    children: [
                        {
                            name: 'workspace-dashboard',
                            path: '',
                            redirect: { name: 'workspace-analytics' },
                            meta: { requiresAuth: true },
                        },
                        {
                            name: 'workspace-settings',
                            path: 'settings',
                            component: () => import('../views/workspace/SettingsView.vue'),
                            meta: { requiresAuth: true, isInMenu: true },
                        },
                        {
                            name: 'workspace-user',
                            path: 'user',
                            component: () => import('../views/workspace/UserView.vue'),
                            meta: { requiresAuth: true, isInMenu: true },
                            children: [
                                {
                                    name: 'workspace-user-invite',
                                    path: 'new',
                                    component: () =>
                                        import('@/components/workspace/WorkspaceInvite.vue'),
                                    meta: { requiresAuth: true },
                                },
                            ],
                        },
                        {
                            name: 'workspace-analytics',
                            path: 'analytics',
                            component: () => import('../views/workspace/AnalyticsView.vue'),
                            meta: { requiresAuth: true, isInMenu: true },
                        },
                        {
                            name: 'workspace-widgets',
                            path: 'widgets',
                            component: () => import('../views/workspace/WidgetsView.vue'),
                            meta: { requiresAuth: true, isInMenu: true },
                            children: [
                                {
                                    name: 'workspace-widget-create',
                                    path: 'new',
                                    component: () =>
                                        import('@/components/workspace/WidgetEditCreate.vue'),
                                    meta: { requiresAuth: true },
                                },
                                {
                                    name: 'workspace-widget-edit',
                                    path: 'edit/:widgetId',
                                    component: () =>
                                        import('@/components/workspace/WidgetEditCreate.vue'),
                                    props: (route) => ({
                                        widgetId: route.params.widgetId,
                                        editMode: true,
                                    }),
                                    meta: { requiresAuth: true },
                                },
                            ],
                        },
                        {
                            name: 'workspace-apikey',
                            path: 'apikey',
                            component: () => import('../views/workspace/ApiKeysView.vue'),
                            meta: { requiresAuth: true, isInMenu: true },
                        },
                    ],
                },
                {
                    name: 'workflows',
                    path: 'workflows',
                    component: () => import('../views/workflows/WorkflowsView.vue'),
                    meta: { requiresAuth: true, isInMenu: true },
                    children: [
                        {
                            name: 'document-categorization',
                            path: 'categorization',
                            component: () =>
                                import('../views/workflows/CategorizationWorkflowView.vue'),
                            meta: { requiresAuth: true },
                        },
                        {
                            name: 'category-create',
                            path: 'categorization/category/new',
                            component: () =>
                                import('../components/categorization/CategoryEditor.vue'),
                            meta: { requiresAuth: true },
                        },
                        {
                            name: 'category-edit',
                            path: 'categorization/category/:id',
                            component: () =>
                                import('../components/categorization/CategoryEditor.vue'),
                            meta: { requiresAuth: true },
                            props: true,
                        },
                    ],
                },
                {
                    path: 'admin',
                    children: [
                        {
                            name: 'admin-dashboard',
                            path: '',
                            component: () => import('../views/admin/AdminDashboard.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'experimental-knowledge-piece-tester',
                            path: 'knowledge-piece-tester',
                            component: () => import('../views/admin/KnowledgePiecesView.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'websocket-tester',
                            path: 'websocketTester',
                            component: () => import('../views/admin/WebsocketTester.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'experts-admin',
                            path: 'experts',
                            component: () => import('../views/admin/AllExpertsAdmin.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'backend-versions',
                            path: 'backend-versions',
                            component: () => import('../views/admin/BackendVersions.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'cron-jobs',
                            path: 'cron-jobs',
                            component: () => import('../views/admin/CronJobs.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'llm-configurations',
                            path: 'llm-configurations',
                            component: () => import('../views/admin/LLMConfigurations.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'subscriptions',
                            path: 'subscriptions',
                            component: () => import('../views/admin/SubscriptionsAdminView.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'websocket-test',
                            path: 'websocket-test',
                            component: () => import('../views/admin/WebSocketTest.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'user-management',
                            path: 'users',
                            component: () => import('../views/admin/UserAdminView.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'workspace-management',
                            path: 'workspaces',
                            component: () => import('../views/admin/WorkspaceAdminView.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'process-overview',
                            path: 'process-overview',
                            component: () => import('../views/admin/ProcessOverview.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'analytics-platform',
                            path: 'admin-analytics',
                            component: () => import('../views/admin/AnalyticsPlattform.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'settings-overview',
                            path: 'settings',
                            component: () => import('../views/admin/SettingsOverview.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'llm-tasks',
                            path: 'llm-tasks',
                            component: () => import('../views/admin/LLMTaskOverview.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'api-keys',
                            path: 'api-keys',
                            component: () => import('../views/admin/AdminApiKeysView.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'widget-test',
                            path: 'widget-test',
                            component: () => import('../views/admin/WidgetPreview.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            path: 'evaluation',
                            children: [
                                {
                                    name: 'evaluation-view',
                                    path: '',
                                    component: () => import('../views/admin/EvaluationView.vue'),
                                    meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                                },
                                {
                                    name: 'evaluation-details',
                                    path: ':evaluation_id',
                                    component: () =>
                                        import('../views/admin/EvaluationDetailsView.vue'),
                                    meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                                },
                            ],
                        },

                        {
                            name: 'question-context-retrieval',
                            path: 'question-context-retrieval',
                            component: () =>
                                import('../views/admin/QuestionContextRetrievalView.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'api-features',
                            path: 'api-features',
                            component: () => import('../views/admin/ApiFeatures.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                        {
                            name: 'polit-chat-dashboard',
                            path: 'polit-chat/:config_id',
                            component: () => import('../views/politchat/PolitChatDashboard.vue'),
                            meta: { requiresAuth: true, isInMenu: false, adminMode: true },
                        },
                    ],
                },
                {
                    name: 'source-viewer',
                    path: 'expert/:expertId/:sourceType/:sourceId',
                    component: () => import('../views/expert/SourceViewer.vue'),
                    meta: { requiresAuth: true, isInMenu: false },
                    props: true,
                },
            ],
        },
        {
            name: 'password-reset',
            path: '/password_reset/:searchText',
            redirect: (to) => {
                // the function receives the target route as the argument
                // we return a redirect path/location here.
                return { path: '/search', query: { q: to.params.searchText } };
            },
        },
        {
            path: '/:pathMatch(.*)*', // catch all 404
            component: () => import('../views/NotFoundView.vue'),
        },
    ],
});

router.beforeEach(async (to, from, next) => {
    const user_store = useUserStore();
    // Check if route requires authentication
    if (to.matched.some((record) => record.meta.requiresAuth)) {
        // If already logged in
        if (user_store.is_logged_in) {
            // Check if route requires admin mode
            if (to.matched.some((record) => record.meta.adminMode)) {
                if (user_store.is_superuser) {
                    user_store.enable_super_mode();
                    return next();
                }
                return next('/home');
            }
            return next();
        }

        // Not logged in - try auto login
        try {
            const loginSuccess = await user_store.auto_login();

            if (!loginSuccess) {
                // Auto login failed, redirect to login page
                return next('/login');
            }
            // Auto login successful, check admin routes again
            if (to.matched.some((record) => record.meta.adminMode)) {
                if (user_store.is_superuser) {
                    user_store.enable_super_mode();
                    return next();
                }
                return next('/home');
            }
            return next();
        } catch (error) {
            console.error('Auto login error:', error);
            return next('/login');
        }
    }

    // Route doesn't require auth
    return next();
});

export default router;
